import { Route2g, RouteStop } from "@/reducers/routes2gSlice"
import { Element } from "@xmpp/xml"

class Route2gFromFormParser {
  static readonly NAMESPACE = "jabber:x:data"

  static parse(stanza: Element): Route2g | undefined {
    const form = stanza.getChild("x", this.NAMESPACE)
    if (!form) {
      return undefined
    }

    const stops = form
      .getChildren("item")
      .map((item) => this.parseItemAsStop(item))
      .filter((stop): stop is RouteStop => stop !== undefined)

    const driverJidField = form.getChildByAttr("var", "driver_jid")
    const driverJid = driverJidField?.getChildText("value")

    // A route must have a driver JID to be valid.
    if (!driverJid) {
      return undefined
    }

    return {
      jid: driverJid,
      stops: stops,
    }
  }

  static parseItemAsStop(item: Element): RouteStop | undefined {
    const address = this.getFieldValue(item, "address")

    // A stop is not valid without an address.
    if (!address) {
      return undefined
    }

    const sourceMetadataValue = this.getFieldValue(item, "source_metadata")
    let sourceMetadata: Record<string, unknown> | undefined
    if (sourceMetadataValue) {
      try {
        sourceMetadata = JSON.parse(sourceMetadataValue) as Record<
          string,
          unknown
        >
      } catch (e) {
        console.error("Failed to parse source_metadata JSON:", e)
        sourceMetadata = undefined
      }
    }

    return {
      id: this.getFieldValue(item, "id") || undefined,
      location: {
        formattedAddress: address,
        companyName: this.getFieldValue(item, "company_name") || undefined,
        postalCode: this.getFieldValue(item, "postal_code") || undefined,
        street: this.getFieldValue(item, "street") || undefined,
        locality: this.getFieldValue(item, "locality") || undefined,
        country: this.getFieldValue(item, "country") || undefined,
        countryCode: this.getFieldValue(item, "country_code") || undefined,
        lat: this.getFieldValue(item, "lat") || undefined,
        lon: this.getFieldValue(item, "lng") || undefined,
      },
      msgId: this.getFieldValue(item, "msg-id") || undefined,
      addressUpdated: this.parseBooleanIfNotNull(
        this.getFieldValue(item, "address-updated"),
      ),
      action: this.getFieldValue(item, "action") || undefined,
      action_reference_number:
        this.getFieldValue(item, "action_reference_number") || undefined,
      ref: this.getFieldValue(item, "ref") || undefined,
      scheduled_arrival_time:
        this.getFieldValue(item, "scheduled_arrival_time") || undefined,
      scheduled_hour_min:
        this.getFieldValue(item, "scheduled_hour_min") || undefined,
      scheduled_hour_max:
        this.getFieldValue(item, "scheduled_hour_max") || undefined,
      time_fixed: this.parseBooleanIfNotNull(
        this.getFieldValue(item, "time_fixed"),
      ),
      shouldAlert: this.parseBooleanIfNotNull(
        this.getFieldValue(item, "should_alert"),
      ),
      pallet_count: this.getFieldValue(item, "pallet_count") || undefined,
      weight_kg: this.getFieldValue(item, "weight_kg") || undefined,
      alertMetadata: this.getFieldValue(item, "alert_metadata") || undefined,
      route_handle: this.getFieldValue(item, "route_handle") || undefined,
      source_metadata: sourceMetadata,
      external_status: this.getFieldValue(item, "external_status") || undefined,
      external_status_at:
        this.getFieldValue(item, "external_status_at") || undefined,
      external_status_source:
        this.getFieldValue(item, "external_status_source") || undefined,
    }
  }

  private static getFieldValue(
    parentElement: Element,
    varName: string,
  ): string | null {
    const field = parentElement.getChildByAttr("var", varName)
    // The value is inside a <value> child of the <field>
    return field?.getChildText("value") ?? null
  }

  private static parseBooleanIfNotNull(
    value: string | null,
  ): boolean | undefined {
    if (value === null || value === undefined) return undefined

    const lowercaseValue = value.toLowerCase()

    if (
      lowercaseValue === "true" ||
      lowercaseValue === "1" ||
      lowercaseValue === "yes"
    ) {
      return true
    }
    if (
      lowercaseValue === "false" ||
      lowercaseValue === "0" ||
      lowercaseValue === "no"
    ) {
      return false
    }

    return undefined
  }
}

export default Route2gFromFormParser
